/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { LemmonModal, Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { StockItem } from "../Stock/types";
import type { Count, CountEntry } from "./types";
import { CountLineRow, countedQty } from "./CountLineRow";

type Props = {
  countId: number | null;
  onClose: (changed: boolean) => void;
};

// Count what's on the shelf. Only items with something entered are saved —
// an item left blank isn't counted (it is not treated as zero). Posting
// adjusts stock to what was counted.
// See docs/plans/inventory_and_costing/06_capture.md (#7).
export const CountEditor = defineComponent<Props>({
  name: "CountEditor",
  props: ["countId", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(true);
    const busy = ref(false);
    const changed = ref(false);
    const id = ref<number | null>(props.countId);
    const status = ref<"draft" | "posted">("draft");
    const name = ref("");
    const items = ref<StockItem[]>([]);
    const entries = ref<Record<number, CountEntry[]>>({});
    const expectedFromCount = ref<Record<number, number>>({});
    const category = ref("");
    const search = ref("");

    const readOnly = computed(() => status.value === "posted");

    const applyCount = (count: Count) => {
      id.value = count.id;
      status.value = count.status;
      name.value = count.name ?? "";
      for (const line of count.lines) {
        entries.value[line.stock_item_id] = line.entries.map((e) => ({ qty: Number(e.qty), unit: e.unit }));
        expectedFromCount.value[line.stock_item_id] = line.expected_qty;
      }
    };

    onMounted(async () => {
      try {
        const [stock, count] = await Promise.all([
          trpc.stock.list.query(),
          props.countId ? trpc.stock.count.query({ id: props.countId }) : null,
        ]);
        items.value = (stock as StockItem[]).filter((s) => s.active);
        if (count) applyCount(count as Count);
      } catch {
        toast.showToast(t("inventory.counts.load_failed"), { type: "error" });
        props.onClose(false);
      } finally {
        loading.value = false;
      }
    });

    const entriesFor = (item: StockItem) =>
      entries.value[item.id] ?? [{ qty: NaN, unit: item.units[item.units.length - 1]?.name ?? item.base_unit }];

    const expectedFor = (item: StockItem) =>
      readOnly.value ? (expectedFromCount.value[item.id] ?? 0) : item.qty_on_hand;

    const categories = computed(() =>
      Array.from(new Set(items.value.map((i) => i.stock_category).filter((c): c is string => !!c))).sort(),
    );

    const visible = computed(() =>
      items.value.filter((i) => {
        if (readOnly.value && !entries.value[i.id]) return false;
        if (category.value && i.stock_category !== category.value) return false;
        return !search.value || i.name.toLowerCase().includes(search.value.toLowerCase());
      }),
    );

    const countedLines = computed(() =>
      items.value
        .map((item) => ({
          item,
          entries: (entries.value[item.id] ?? []).filter((e) => !Number.isNaN(e.qty) && e.qty >= 0),
        }))
        .filter((l) => l.entries.length > 0),
    );

    const totalValue = computed(() =>
      countedLines.value.reduce((sum, { item, entries: e }) => {
        const counted = countedQty(item, e) ?? 0;
        return sum + (counted - expectedFor(item)) * item.avg_cost;
      }, 0),
    );

    const save = async () => {
      const saved = await trpc.stock.saveCount.mutate({
        id: id.value ?? undefined,
        name: name.value || null,
        lines: countedLines.value.map(({ item, entries: e }) => ({ stock_item_id: item.id, entries: e })),
      });
      changed.value = true;
      applyCount(saved as Count);
      return saved as Count;
    };

    const run = async (action: () => Promise<void>, failKey: string) => {
      busy.value = true;
      try {
        await action();
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t(failKey), { type: "error" });
      } finally {
        busy.value = false;
      }
    };

    const handleSave = () =>
      run(async () => {
        await save();
        toast.showToast(t("inventory.counts.saved"), { type: "success" });
      }, "inventory.counts.save_failed");

    const handlePost = () =>
      run(async () => {
        if (!confirm(t("inventory.counts.post_confirm"))) return;
        const saved = await save();
        await trpc.stock.postCount.mutate({ id: saved.id });
        toast.showToast(t("inventory.counts.posted"), { type: "success" });
        props.onClose(true);
      }, "inventory.counts.post_failed");

    const renderBody = () => {
      if (loading.value) {
        return (
          <div class="flex justify-center p-10">
            <Loading />
          </div>
        );
      }
      return (
        <div class="space-y-3 p-6">
          <div class="flex flex-wrap gap-2">
            <input
              class="input input-bordered input-sm w-48"
              placeholder={t("inventory.counts.name_placeholder")}
              disabled={readOnly.value}
              v-model={name.value}
            />
            <select class="select select-bordered select-sm" v-model={category.value}>
              <option value="">{t("inventory.counts.all_categories")}</option>
              {categories.value.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input
              class="input input-bordered input-sm w-40"
              placeholder={t("inventory.counts.search")}
              v-model={search.value}
            />
          </div>
          <div class="max-h-[55vh] overflow-auto">
            <table class="table table-sm">
              <thead class="sticky top-0 bg-base-100">
                <tr>
                  <th>{t("inventory.recipe.col_item")}</th>
                  <th>{t("inventory.counts.expected")}</th>
                  <th>{t("inventory.counts.counted")}</th>
                  <th class="text-right">{t("inventory.counts.difference")}</th>
                  <th class="text-right">{t("inventory.counts.value")}</th>
                </tr>
              </thead>
              <tbody>
                {visible.value.map((item) => (
                  <CountLineRow
                    key={item.id}
                    item={item}
                    entries={entriesFor(item)}
                    expected={expectedFor(item)}
                    readOnly={readOnly.value}
                    onChange={(next: CountEntry[]) => (entries.value = { ...entries.value, [item.id]: next })}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div class="flex items-center justify-between border-t border-base-300 pt-3 text-sm">
            <span class="text-base-content/60">
              {t("inventory.counts.items_counted").replace("{count}", String(countedLines.value.length))}
            </span>
            <span>
              {t("inventory.counts.total_difference")}{" "}
              <span class={["font-semibold", totalValue.value < 0 ? "text-error" : ""].join(" ")}>
                {totalValue.value.toFixed(2)}
              </span>
            </span>
          </div>
        </div>
      );
    };

    return () => (
      <LemmonModal
        title={readOnly.value ? t("inventory.counts.posted_title") : t("inventory.counts.title")}
        onClose={() => props.onClose(changed.value)}
        renderButtons={() => (
          <div class="border-t p-4">
            <div class="flex justify-between gap-2">
              <button class="btn btn-neutral" onClick={() => props.onClose(changed.value)}>
                {readOnly.value ? t("inventory.purchases.close") : t("general.words.cancel")}
              </button>
              {!readOnly.value && (
                <div class="flex gap-2">
                  <button class="btn btn-ghost" disabled={loading.value || busy.value} onClick={handleSave}>
                    {t("inventory.purchases.save_draft")}
                  </button>
                  <button
                    class="btn btn-primary"
                    disabled={loading.value || busy.value || countedLines.value.length === 0}
                    onClick={handlePost}
                  >
                    {t("inventory.counts.post")}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      >
        {renderBody()}
      </LemmonModal>
    );
  },
});
